import Vue from 'vue';

const moment = require('moment');

const SessionHandler = {
    /**
     * Starts the session check timer.
     *
     * @param context
     * @param vm
     */
    startSessionCheckTimer(context, vm) {
        const id = setInterval(function () {
            context.dispatch('showSessionDialog', vm);
        }, vm.sessionCheckFrequency);
    },

    /**
     *
     * @param context
     * @param vm
     * @returns {Promise<boolean>}
     */
    showSessionDialog(context, vm) {
        if (SessionHandler.isNotLoggedIn(context)) {
            return Promise.resolve(null);
        }

        const now = moment();

        // console.log(`Running show session dialog at ${now.format('hh:mm:ss')}.`);

        // Use the stored current login time plus the duration in seconds in case the client's clock is not set
        // correctly.
        const storedCurrentLoginTime = moment(context.state.currentLoginTime);

        const expiresInSeconds = context.state.authToken.refresh_token_expires_in;
        const refreshTokenExpiresAt = storedCurrentLoginTime.add(expiresInSeconds, 'seconds');

        /**
         * Set the number of seconds the warning popup will appear.
         *
         * @note If you change this in the .env you must restart any `npm watch run` process or the change will not
         * be recognised.
         *
         * @type {string|number}
         */
        const sessionsAppearSecondsBefore = process.env.MIX_SHOW_SESSION_TIMEOUT || 90;

        const showWarningWhen = moment(refreshTokenExpiresAt).subtract(sessionsAppearSecondsBefore, 'seconds');
        const shouldShowWarning = now > showWarningWhen;

        // Let us keep this here commented: this is very useful for debugging / testing but too chatty for production.
        // window.console.log(`token expires at ${refreshTokenExpiresAt.format()}\n`
        //     + `show warning at ${showWarningWhen.format()}\nnow ${now.format()}`);
        // window.console.log(`Auth token expires at: ${context.state.authToken.expires_at}; `
        //     + `calculated: ${calculatedAuthTokenExpiresAt.format()}`);

        const sessionHasExpired = now > refreshTokenExpiresAt;

        if (vm.$swal.isVisible()) {
            if (!sessionHasExpired) {
                return Promise.resolve(null);
            }

            window.console.log('Session has expired - displaying expired dialog.');
        }

        if (shouldShowWarning) {
            let message = `You will be logged out ${refreshTokenExpiresAt.from(now)} if you do not click Ok.`;

            // window.console.log(`Outer session expired ${sessionHasExpired}`);

            /*
             * The below code needs to be refactored however as we keep spinning around on this issue, I'm not going
             * to tidy it up until upstream believes they might accept the proposed solution.
             *
             * @todo Refactor this ridiculous mess.
             *
             * At first I attempted to only show the dialog if no other window was shown; this is the purpose of the
             * `setSessionPopup` code; I did this by communicating with the child window. However, if you closed the
             * parent window, the dialog could no longer communicate with the application.
             *
             * It doesn't appear to be easy to communicate with all the possible browser windows and tabs unless you
             * are the browser itself. Things such as `postMessage`
             *  (https://developer.mozilla.org/en-US/docs/Web/API/Window/postMessage) require you to know the window
             * to communicate with.
             *
             * Now, you say, use the Websockets...but we can't because we can't tell which browser is which. Not only
             * that we would need the web-sockets to talk to the dialogs themselves because if the dialogs are no longer
             * modal then we can't guarantee we've got some parent-like or container-like thing to communicate to the
             * server.
             *
             * So, at the moment:
             *
             * 1. If there is an upcoming warning it will attempt to open a window rather than a sweet alert
             * 2. The window is not branded for Kaoshi, looks horrible, might be blocked, could appear in a tab
             * 3. Worse it's very difficult, in the presence of tabs/multiple open browsers of the same type, to tell
             *    if there is a popup already without the popup somewhat sharing the Kaoshi code
             *
             * Now, you say, just make it share some or all of the code. Sure. I did that. That's what the
             *  session-timeout.vue is. However, this code would need to suffer a divorce from the main code because it
             * ITSELF is subject to the session timeout code and whilst it doesn't get itself into an endless loop
             * it does other unexpected stuff.
             *
             * PLUS it suffers from all the same issues as trying to keep one Window open; it's a little more robust
             * because theoretically it could -itself- manipulate the local storage / Vuex store people can still
             * summarily just press the `x` button and you're back to square one.
             *
             * THERE IS A REASON WHY MODALS ARE NOW DONE BY DIVS WITHIN THE SAME WEB-PAGE...
             *
             * In the presence of all these issues, we could just see if this gets approval (it does NOT _look_ better).
            */
            let windowOpened;
            if (!sessionHasExpired) {
                const theWidth = 600;
                const theHeight = 350;
                const top = (window.outerHeight / 2) - (theHeight / 2);
                const left = (window.outerWidth / 2) - (theWidth / 2);

                const windowOptions = `resizable,toolbar=no,location=no,scrollbars=no,menubar=no,width=${theWidth},`
                    + `height=${theHeight},top=${top},left=${left}`;

                windowOpened = window.open('/session-timeout.html', 'com.kaoshi.session-timeout', windowOptions);

                if (windowOpened) {
                    windowOpened.focus();
                }

                window.sessionHandlerCancel = () => {
                    vm.$swal.close();
                };

                window.sessionHandlerPing = () => {
                    // @todo Get rid of this DRY.
                    const userId = context.state.user.id;

                    const innerNow = moment();
                    const innerSessionHasExpired = innerNow > refreshTokenExpiresAt;

                    // console.log(`Inner session expired: ${innerSessionHasExpired}`);

                    if (!innerSessionHasExpired) {
                        vm.$swal.close();

                        if (SessionHandler.isNotLoggedIn(context)) {
                            if (window.location === '/') {
                                window.location = '/login';
                            } else {
                                window.location = '/';
                            }

                            return false;
                        }

                        Vue.backendApi().user(userId).ping().then(() => true)
                            .catch((error) => {
                                console.trace(error);
                                return false;
                            });
                    } else {
                        context.commit('setHandlingSession', true);
                    }

                    return true;
                };
            }

            if (sessionHasExpired) {
                context.commit('setHandlingSession', true);
                message = `Your session ended ${refreshTokenExpiresAt.from(now)}. Please login again after clicking `
                    + 'OK if you want to continue to use Kaoshi Network.';
            }

            const alertConfig = {
                toast: false,
                showCancelButton: !sessionHasExpired,
                showConfirmButton: true,

                backdrop: true, // Make it a modal for the tab/window.
                allowOutsideClick: false, // This makes it a little harder to ignore the dialog.

                type: 'info',
                title: 'Session Check',
                text: message,
            };

            if (sessionHasExpired || (!windowOpened)) {
                // eslint-disable-next-line prefer-arrow-callback
                vm.$swal.fire(alertConfig).then(function (result) {
                    if (result.hasOwnProperty('value') && result.value === true) {
                        const userId = context.state.user.id;

                        if (!context.state.authToken || !context.state.authToken.expires_at
                            || !context.state.user || !context.state.user.id) {
                            if (vm.$route.meta && vm.$route.meta.forceLogin) {
                                window.location = `/login?redirectTo=${vm.$route.fullPath}`;
                            } else if (window.location === '/') {
                                window.location = '/login';
                            } else {
                                window.location = '/';
                            }

                            return false;
                        }

                        Vue.backendApi().user(userId).ping().then(() => true)
                            .catch((error) => {
                                console.trace(error);
                                context.dispatch('auth/logout');
                                return false;
                            });
                    }
                }).catch((error) => {
                    console.error(error);
                });

                return Promise.resolve(true);
            }
        }

        return Promise.resolve(false);
    },

    /**
     * Refresh the token, if available.
     *
     * @note This is called from the store to refresh the token.
     *
     * @param context
     * @returns {Promise<void>}
     */
    async refreshToken(context) {
        try {
            const kaoshiMoney = JSON.parse(window.localStorage.getItem('kaoshi-money'));
            const { authToken } = kaoshiMoney;

            const payload = { refresh_token: authToken.refresh_token };
            // There is no need to add an interceptor here; it is handled in Vue.backendApi.
            const result = await Vue.backendApi().auth().refreshLogin(payload);

            const token = result.data;
            context.commit('storeAuthToken', token);

            return Promise.resolve();
        } catch (error) {
            return Promise.reject(error);
        }
    },

    /**
     * Determines if the user is logged in, given a context (i.e. a user state from Vuex).
     *
     * @param context
     * @returns {*|boolean}
     */
    isNotLoggedIn(context) {
        return context && (!context.state.authToken || !context.state.authToken.expires_at
            || !context.state.user || !context.state.user.id);
    },
};

export default SessionHandler;
